// src/app/new-job.jsx
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  KeyboardAvoidingView,
  ScrollView,
  Keyboard,
  StyleSheet,
  TouchableWithoutFeedback,
  Platform,
  TouchableOpacity,
  Modal,
} from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MdAdd, MdRemove } from 'react-icons/md';
import { format } from 'date-fns';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// Helper to fetch a token from localStorage
const getToken = () => {
  try {
    return localStorage.getItem('userToken');
  } catch {
    return null;
  }
};

// Format a Date -> 'YYYY-MM-DDTHH:mm' for <input type="datetime-local">
const toLocalDatetimeInput = (d) => {
  if (!(d instanceof Date) || isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

export default function NewJob() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [customerName, setCustomerName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');

  // Keep date & time as Date objects; post as separate date/time strings
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    return d;
  });
  const [selectedTime, setSelectedTime] = useState(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    return d;
  });

  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  const [isAddCustomerVisible, setAddCustomerVisible] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
  });

  const [visitType, setVisitType] = useState(''); // 'job' or 'quote'
  const [isNotesVisible, setIsNotesVisible] = useState(false);
  const [notes, setNotes] = useState('');
  const [users, setUsers] = useState([]);
  const [assignedUserIds, setAssignedUserIds] = useState(['all']); // Default to 'all' for consistency
  const [subscription, setSubscription] = useState('');
  const [isUserPickerVisible, setUserPickerVisible] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);

  // Seed from URL params (e.g., ?customerName=John&date=2025-09-01T14:00&quote=true)
  useEffect(() => {
    const pCustomer = searchParams.get('customerName');
    const pDate = searchParams.get('date');
    const pQuote = searchParams.get('quote');
    const pJob = searchParams.get('job');

    if (pCustomer) {
      setCustomerName(pCustomer);
      setSearchQuery(pCustomer);
    }
    if (pDate) {
      const parsed = new Date(pDate);
      if (!isNaN(parsed)) {
        parsed.setSeconds(0, 0);
        setSelectedDate(parsed);
        setSelectedTime(parsed);
      }
    }
    if (!visitType) {
      if (pQuote) setVisitType('quote');
      else if (pJob) setVisitType('job');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Load customers
  useEffect(() => {
    (async () => {
      try {
        const token = getToken();
        const res = await axios.get(`${API_BASE_URL}/api/customers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data || [];
        setCustomers(list);

        // If param customerName exists in list, clear suggestions
        const pCustomer = searchParams.get('customerName');
        if (pCustomer && list.some((c) => c.name === pCustomer)) {
          setFilteredCustomers([]);
        }
      } catch (err) {
        console.error('Error fetching customers:', err);
        window.alert('Failed to load customers.');
      }
    })();
  }, [searchParams]);

  // Load user permissions and users if subscription is free or pro
useEffect(() => {
  const loadUserPermissions = () => {
    try {
      const permissions = localStorage.getItem('userPermissions');
      if (permissions) {
        const parsedPermissions = JSON.parse(permissions);
        setUserPermissions(parsedPermissions);

        const user_type = parsedPermissions?.user_type;
        const jobPerm = parsedPermissions?.job_perm;

        if (user_type !== 'Owner' && user_type !== 'Admin' && jobPerm !== 'write') {
          alert("You don't have permission to create jobs. Please contact your account administrator.");
          navigate('/'); // Redirect to home page
          return;
        }
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
    }
  };

  (async () => {
    try {
      const token = getToken();
      const sub = localStorage.getItem('subscription');
      setSubscription(sub);

      // Only fetch users if subscription is 'free' or 'pro'
      if (sub === 'free' || sub === 'pro') {
        const response = await axios.get(`${API_BASE_URL}/api/getAllUsers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(response.data || []);

        // Set default to 'All' users
        setAssignedUserIds(['all']);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  })();

  loadUserPermissions();
}, [navigate]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredCustomers([]);
      return;
    }
    const matches = customers.filter((c) =>
      (c.name || '').toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCustomers(matches);
  };

  const handleAddCustomer = async () => {
    try {
      const token = getToken();
      const response = await axios.post(`${API_BASE_URL}/api/customers`, newCustomer, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const added = response.data;
      const updated = [...customers, added];
      setCustomers(updated);

      setCustomerName(newCustomer.name);
      setSearchQuery(newCustomer.name);
      setFilteredCustomers([]);
      setAddCustomerVisible(false);
      setNewCustomer({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: '',
      });
    } catch (error) {
      console.error('Error adding customer:', error);
      window.alert('Failed to add customer. Please try again.');
    }
  };

  const toggleUserSelection = (userId) => {
    if (userId === 'all') {
      // If clicking 'All', select only 'all'
      setAssignedUserIds(['all']);
    } else {
      // If clicking a specific user, remove 'all' from selection
      const currentIds = assignedUserIds.filter(id => id !== 'all');
      
      if (currentIds.includes(userId)) {
        const newIds = currentIds.filter((id) => id !== userId);
        // If no users left, default back to 'all'
        setAssignedUserIds(newIds.length > 0 ? newIds : ['all']);
      } else {
        setAssignedUserIds([...currentIds, userId]);
      }
    }
  };

  const validateAndSaveJob = async () => {
    // Check if visit type is selected
    if (!visitType) {
      window.alert('Please select either Job or Quote before saving.');
      return;
    }

    const selected = customers.find((c) => c.name === customerName);
    if (!selected) {
      window.alert('The customer does not exist. Please add the customer first.');
      return;
    }
    if (!title.trim()) {
      window.alert('Please enter a title.');
      return;
    }
    await saveJob();
  };

  const saveJob = async () => {
    try {
      const token = getToken();

      // Handle price: if empty or invalid, default to '0.00', otherwise trim whitespace
      let finalPrice = price.trim();
      if (finalPrice === '' || isNaN(parseFloat(finalPrice))) {
        finalPrice = '0.00';
      }

      const adjustedPrice = visitType === 'quote' ? '0.00' : finalPrice;

      // Determine assigned_type and assignedUserIds based on selection
      let assigned_type = null;
      let userIdsToSend = [];

      if (assignedUserIds.includes('all')) {
        // All users selected - send 'all' type with empty array
        assigned_type = 'all';
        userIdsToSend = [];
      } else {
        // Specific users selected - send 'specific' type with user IDs
        assigned_type = 'specific';
        userIdsToSend = assignedUserIds.map((id) => parseInt(id));
      }

      const payload = {
        customerName,
        title: title.trim(),
        description: description.trim() || '', // Save as empty string if blank
        price: adjustedPrice,
        date: selectedDate.toISOString().split('T')[0], // YYYY-MM-DD (UTC)
        time: selectedTime.toISOString().split('T')[1].slice(0, 8), // HH:mm:ss (UTC)
        completed: 'no',
        visitType,
        notes: notes.trim() || '', // Save as empty string if blank
        assigned_type: assigned_type,
        assignedUserIds: userIdsToSend,
      };

      await axios.post(`${API_BASE_URL}/api/jobs`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      navigate('/schedule', { replace: true });
    } catch (error) {
      console.error('Error saving job:', error);
      window.alert('Failed to save job.');
    }
  };

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={0}
    >
      <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
        <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
          <View style={styles.container}>
            <Text style={styles.label}>Select Visit Type</Text>
            <View style={styles.selectionContainer}>
              <TouchableOpacity
                style={[styles.selectionButton, visitType === 'job' && styles.selectedButton]}
                onPress={() => setVisitType('job')}
              >
                <Text
                  style={[
                    styles.selectionButtonText,
                    visitType === 'job' && styles.selectedButtonText,
                  ]}
                >
                  Job
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.selectionButton, visitType === 'quote' && styles.selectedButton]}
                onPress={() => setVisitType('quote')}
              >
                <Text
                  style={[
                    styles.selectionButtonText,
                    visitType === 'quote' && styles.selectedButtonText,
                  ]}
                >
                  Quote
                </Text>
              </TouchableOpacity>
            </View>

            <Text style={styles.label}>Customer Name</Text>
            <input
              type="text"
              style={styles.webInput}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onBlur={() => {
                if (customers.some((c) => c.name === searchQuery)) {
                  setCustomerName(searchQuery);
                  setFilteredCustomers([]);
                }
              }}
              placeholder="Search or add customer"
            />

            {searchQuery ? (
              <View style={styles.searchResults}>
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((c, idx) => (
                    <TouchableOpacity
                      key={`${c.id || c.name}-${idx}`}
                      onPress={() => {
                        setCustomerName(c.name);
                        setSearchQuery(c.name);
                        setFilteredCustomers([]);
                      }}
                    >
                      <Text style={styles.searchResult}>{c.name}</Text>
                    </TouchableOpacity>
                  ))
                ) : !customers.some((c) => c.name === searchQuery) ? (
                  <View style={styles.addCustomerRow}>
                    <Text style={styles.searchResult}>{searchQuery}</Text>
                    <TouchableOpacity
                      style={styles.addCustomerButton}
                      onPress={() => {
                        setNewCustomer({ ...newCustomer, name: searchQuery });
                        setAddCustomerVisible(true);
                      }}
                    >
                      <MdAdd size={20} color="green" />
                      <Text style={styles.addCustomerText}>Add Customer</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
            ) : null}

            {/* Add Customer Modal */}
            <Modal visible={isAddCustomerVisible} animationType="slide" onRequestClose={() => setAddCustomerVisible(false)}>
              <KeyboardAvoidingView
                style={{ flex: 1 }}
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                keyboardVerticalOffset={100}
              >
                <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
                  <ScrollView contentContainerStyle={styles.modalScrollContainer} keyboardShouldPersistTaps="handled">
                    <View style={styles.modalContainer}>
                      <Text style={styles.modalHeader}>Add Customer</Text>

                      <Text style={styles.modalLabel}>Customer Name</Text>
                      <input
                        type="text"
                        style={styles.webInput}
                        value={newCustomer.name}
                        onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                        placeholder="Customer Name"
                      />

                      <Text style={styles.modalLabel}>Address</Text>
                      <input
                        type="text"
                        style={styles.webInput}
                        value={newCustomer.address}
                        onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                        placeholder="Address"
                      />

                      <Text style={styles.modalLabel}>City</Text>
                      <input
                        type="text"
                        style={styles.webInput}
                        value={newCustomer.city}
                        onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                        placeholder="City"
                      />

                      <Text style={styles.modalLabel}>State</Text>
                      <input
                        type="text"
                        style={styles.webInput}
                        value={newCustomer.state}
                        onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value })}
                        placeholder="State"
                      />

                      <Text style={styles.modalLabel}>Zip</Text>
                      <input
                        type="text"
                        style={styles.webInput}
                        value={newCustomer.zip}
                        onChange={(e) => setNewCustomer({ ...newCustomer, zip: e.target.value })}
                        placeholder="Zip"
                      />

                      <Text style={styles.modalLabel}>Phone</Text>
                      <input
                        type="text"
                        style={styles.webInput}
                        value={newCustomer.phone}
                        onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                        placeholder="Phone"
                      />

                      <Text style={styles.modalLabel}>Email</Text>
                      <input
                        type="email"
                        style={styles.webInput}
                        value={newCustomer.email}
                        onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                        placeholder="Email"
                      />

                      <TouchableOpacity style={styles.saveButton} onClick={handleAddCustomer}>
                        <Text style={styles.saveButtonText}>Save Customer</Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        style={styles.returnButton}
                        onClick={() => setAddCustomerVisible(false)}
                      >
                        <Text style={styles.returnButtonText}>Return To Job</Text>
                      </TouchableOpacity>
                    </View>
                  </ScrollView>
                </TouchableWithoutFeedback>
              </KeyboardAvoidingView>
            </Modal>

            <Text style={styles.label}>Title</Text>
            <input 
              type="text"
              style={styles.webInput} 
              value={title} 
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title"
            />

            <Text style={styles.label}>Description</Text>
            <textarea
              style={{...styles.webInput, ...styles.textArea}}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
            />

            {visitType === 'job' && (
              <>
                <Text style={styles.label}>Price</Text>
                <input
                  type="text"
                  style={styles.webInput}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                />
              </>
            )}

            <Text style={styles.label}>Date & Time</Text>
            <View style={styles.dateTimeRow}>
              {/* Show a human-friendly preview */}
              <Text style={styles.dateTimePreview}>
                {format(selectedDate, 'MMMM dd, yyyy hh:mm a')}
              </Text>
              {/* Web datetime-local input */}
              <input
                type="datetime-local"
                value={toLocalDatetimeInput(selectedDate)}
                onChange={(e) => {
                  const next = new Date(e.target.value);
                  if (!isNaN(next)) {
                    next.setSeconds(0, 0);
                    setSelectedDate(next);
                    setSelectedTime(next);
                  }
                }}
                style={styles.webDateTimeInput}
              />
            </View>

            {subscription === 'free' || subscription === 'pro' ? (
              <>
                <Text style={styles.label}>Assign to:</Text>
                <TouchableOpacity 
                  onPress={() => setUserPickerVisible(true)} 
                  style={styles.assignedUsersContainer}
                >
                  <Text style={styles.assignedUsersText}>
                    {assignedUserIds.includes('all')
                      ? 'All'
                      : users
                          .filter((u) => assignedUserIds.includes(u.id.toString()))
                          .map((u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email)
                          .join(', ')}
                  </Text>
                </TouchableOpacity>
                
                {isUserPickerVisible && (
                  <View style={styles.overlay}>
                    <View style={styles.userPickerCard}>
                      <View style={styles.userPickerHeader}>
                        <Text style={styles.modalTitle}>Select Users</Text>
                        <TouchableOpacity onPress={() => setUserPickerVisible(false)} style={styles.closeButton}>
                          <Text style={styles.closeButtonText}>✕</Text>
                        </TouchableOpacity>
                      </View>
                      <ScrollView style={styles.userListScroll}>
                        {/* All option */}
                        <TouchableOpacity
                          style={styles.userOption}
                          onPress={() => toggleUserSelection('all')}
                        >
                          <Text style={styles.userOptionText}>All</Text>
                          {assignedUserIds.includes('all') && (
                            <Text style={styles.checkMark}>✓</Text>
                          )}
                        </TouchableOpacity>
                        
                        {/* Individual users */}
                        {users.map((user) => {
                          const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
                          const isSelected = assignedUserIds.includes(user.id.toString());
                          return (
                            <TouchableOpacity
                              key={user.id}
                              style={styles.userOption}
                              onPress={() => toggleUserSelection(user.id.toString())}
                            >
                              <Text style={styles.userOptionText}>{fullName}</Text>
                              {isSelected && (
                                <Text style={styles.checkMark}>✓</Text>
                              )}
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                      <TouchableOpacity
                        style={styles.modalButton}
                        onPress={() => setUserPickerVisible(false)}
                      >
                        <Text style={styles.modalButtonText}>Done</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </>
            ) : null}

            {!isNotesVisible ? (
              <TouchableOpacity
                style={styles.addNotesButton}
                onPress={() => setIsNotesVisible(true)}
              >
                <MdAdd size={20} color="green" />
                <Text style={styles.addNotesText}>Add Notes</Text>
              </TouchableOpacity>
            ) : (
              <>
                <TouchableOpacity
                  style={styles.hideNotesButton}
                  onPress={() => setIsNotesVisible(false)}
                >
                  <MdRemove size={20} color="red" />
                  <Text style={styles.hideNotesText}>Hide Notes</Text>
                </TouchableOpacity>
                <View style={styles.fieldContainer}>
                  <Text style={styles.label}>Notes (for internal use only):</Text>
                  <textarea
                    style={{...styles.webInput, ...styles.textArea}}
                    placeholder="Enter notes here..."
                    onChange={(e) => setNotes(e.target.value)}
                    value={notes}
                  />
                </View>
              </>
            )}

            <View style={styles.buttonContainer}>
              <TouchableOpacity style={styles.saveButtonSmall} onPress={validateAndSaveJob}>
                <Text style={styles.saveButtonText}>Save to Schedule</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.cancelButtonSmall}
                onPress={() => navigate('/jobs')}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </TouchableWithoutFeedback>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { padding: 20, paddingBottom: 50 },
  label: { marginTop: 15, fontWeight: 'bold' },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 8,
    borderRadius: 4,
    marginTop: 5,
    backgroundColor: '#fff',
  },
  textArea: {
    height: 120,
    textAlignVertical: 'top',
  },
  selectionContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  selectionButton: {
    flex: 1,
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    borderWidth: 1,
    borderColor: '#ccc',
    marginHorizontal: 5,
  },
  selectedButton: {
    backgroundColor: '#007BFF',
  },
  selectionButtonText: {
    color: '#000',
    fontSize: 16,
  },
  selectedButtonText: {
    color: '#fff',
    fontSize: 16,
  },
  searchResults: {
    marginTop: 5,
    backgroundColor: '#f9f9f9',
    borderRadius: 4,
    padding: 10,
  },
  searchResult: {
    paddingVertical: 5,
    fontSize: 14,
  },
  addCustomerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  addCustomerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 10,
  },
  addCustomerText: {
    color: 'green',
    marginLeft: 5,
  },
  modalScrollContainer: { flexGrow: 1, justifyContent: 'center' },
  modalContainer: {
    flex: 1,
    padding: 20,
    backgroundColor: '#fff',
    marginTop: 50,
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalHeader: { fontSize: 18, fontWeight: 'bold', marginBottom: 20 },
  modalLabel: { marginTop: 15, fontWeight: 'bold', fontSize: 14 },
  saveButton: {
    backgroundColor: '#007AFF',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 20,
  },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  returnButton: {
    backgroundColor: '#ccc',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 10,
  },
  returnButtonText: { color: '#000', fontSize: 16, fontWeight: 'bold' },
  dateTimeRow: {
    marginTop: 6,
    gap: 8,
  },
  dateTimePreview: {
    fontSize: 14,
    color: '#333',
    marginBottom: 6,
  },
  // basic styling for the native HTML input so it blends in
  webDateTimeInput: {
    padding: 10,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    outline: 'none',
    width: '100%',
  },
  webInput: {
    padding: 10,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    outline: 'none',
    width: '100%',
    marginTop: 5,
  },
  addNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 10 },
  addNotesText: { color: 'green', marginLeft: 5 },
  hideNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 10 },
  hideNotesText: { color: 'red', marginLeft: 5 },
  fieldContainer: { marginTop: 0 },
  cancelButton: {
    backgroundColor: '#FF3B30',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 10,
  },
  cancelButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  webSelect: {
    padding: 10,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    outline: 'none',
    width: '100%',
    marginTop: 5,
  },
  assignedUsersContainer: {
    backgroundColor: '#f9f9f9',
    padding: 12,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    marginTop: 5,
    cursor: 'pointer',
  },
  assignedUsersText: {
    color: '#333',
    fontSize: 16,
  },
  userList: {
    maxHeight: 400,
    marginBottom: 20,
  },
  userOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    cursor: 'pointer',
  },
  userOptionText: {
    fontSize: 16,
    color: '#333',
  },
  checkmark: {
    color: '#28a745',
    fontSize: 20,
    fontWeight: 'bold',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  userPickerCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    width: '80%',
    maxWidth: 500,
    padding: 20,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  },
  userPickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: 24,
    cursor: 'pointer',
  },
  userListScroll: {
    maxHeight: 300,
    overflowY: 'auto',
    marginBottom: 20,
  },
  modalButton: {
    backgroundColor: '#007AFF',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
  },
  modalButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  checkMark: {
    color: '#28a745',
    fontSize: 20,
    fontWeight: 'bold',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  saveButtonSmall: {
    backgroundColor: '#007AFF',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    flex: 1,
    marginRight: 10,
  },
  cancelButtonSmall: {
    backgroundColor: '#FF3B30',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    flex: 1,
  },
});
